<template>
    <div class="notranslate">
        <div v-for="[matchStatType, matchMarkets] in formattedBetBuilderMarkets" v-bind:key="matchStatType">
            <div class="match-marketGroup-content" v-bind:class="{ _live: isLive }">
                <div
                    v-for="[marketType, chunkedMarkets] in matchMarkets"
                    v-bind:key="marketType"
                    class="match-betArea-group"
                    v-bind:class="{ gap: isLastRow(matchStatType) }"
                    v-on:dblclick.prevent.stop
                >
                    <div class="match-betArea-type-title">
                        <p>
                            <span>{{ displayStatTypeName(matchStatType) }} </span>
                            {{ getMarketTypeName(marketType) }}
                        </p>
                    </div>
                    <div
                        v-for="marketList in chunkedMarkets"
                        v-bind:key="marketList[0].matchMarketId"
                        class="match-betArea-type"
                    >
                        <div v-if="isShowPointMarketType(marketType)" class="match-oddArea-oddType">
                            <div v-for="name in getShortName(marketType)" v-bind:key="`${name}`">
                                <p>{{ name }}</p>
                            </div>
                        </div>
                        <div
                            v-for="market in marketList"
                            v-bind:key="market.matchMarketId"
                            class="match-oddArea"
                            v-bind:data-testid="`bet-builder-market-${market.matchMarketId}`"
                        >
                            <div class="match-oddArea-group gap">
                                <Selection
                                    v-for="selection in market.selections"
                                    v-bind:key="selection.id"
                                    v-bind:isDisplayName="!isShowPointMarketType(marketType)"
                                    v-bind:isDisplayShortName="true"
                                    v-bind:selection="selection"
                                    v-bind:match="match"
                                    v-bind:market="market"
                                    v-bind:displaySection="displaySection"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import chunk from 'lodash/chunk';
    import concat from 'lodash/concat';
    import { storeToRefs } from 'pinia';
    import { computed, toRefs, watch } from 'vue';
    import Selection from '@/components/betBuilder/Selection.vue';
    import { displayStatTypeName, getColumnCount, isShowPointMarketType } from '@/components/betBuilder/betBuilderOddsDisplay';
    import type { IMatch } from '@/interface/IMatch';
    import type { IBetBuilderMarket } from '@/interface/betBuilder';
    import { BetBuilderDisplayType, BetBuilderMarket, BetBuilderStatType, DisplaySection } from '@/interface/enum';
    import { SboMarketPointOrderContext } from '@/libraries/composables/SboMarketPointOrderContext';
    import { i18n } from '@/libraries/i18n';
    import { sortBetBuilderMarketsByRecordedOrder, sortBetBuilderMarketsForDisplay } from '@/libraries/oddsApi/betBuilderMarketOrder';
    import { getMarketRuleByMarketType } from '@/libraries/oddsApi/betBuilderMarketRule';
    import { betBuilderToMarketType } from '@/libraries/oddsApi/betBuilderMarketTypeConverter';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';

    const props = withDefaults(defineProps<{
        markets: IBetBuilderMarket[];
        match: IMatch;
        isOpenMoreMarket?: boolean;
        displaySection?: DisplaySection;
    }>(), {
        isOpenMoreMarket: false,
        displaySection: DisplaySection.Unknown,
    });

    const emit = defineEmits<{
        'update:collapseMainCount': [count: number];
    }>();

    const mainMarketType = [
        BetBuilderMarket.SetHandicap,
        BetBuilderMarket.SetTotalGoalsOverUnder,
        BetBuilderMarket.Set1x2,
    ];

    const mainMatchStatType = [
        BetBuilderStatType.FullTime,
        BetBuilderStatType.FirstHalf,
    ];

    const { markets } = toRefs(props);
    const { order: marketPointOrder } = SboMarketPointOrderContext.inject();
    const { isBetBuilderDisplayPriorityEnabled } = storeToRefs(useFeatureToggleStore());

    function addFakeMarket(marketList: IBetBuilderMarket[], expectedLength: number) {
        const padLength = marketList.length % expectedLength === 0
            ? 0
            : expectedLength - (marketList.length % expectedLength);

        return concat(marketList, new Array(padLength).fill({
            matchMarketId: '',
            marketType: { id: '' },
            matchStatType: { id: '' },
            point: '',
            displayPriority: 0,
            displayType: BetBuilderDisplayType.Unknown,
            selections: [],
        }));
    }

    /**
     * @description
     * 1. Add fake market to make sure each row has the same number of markets
     * 2. Split markets into chunks based on column count (e.g. 2 columns -> [[1, 2], [3, 4], [5, 6]])
     */
    function processDisplayMarket(markets: IBetBuilderMarket[], columnCount: number) {
        const padMarkets = addFakeMarket(markets, columnCount);
        return chunk<IBetBuilderMarket>(padMarkets, columnCount);
    }

    function getMarketsForType(marketType: BetBuilderMarket, statType: BetBuilderStatType) {
        const rawMarkets = markets.value.filter(market => market.marketType.id === marketType && market.matchStatType.id === statType);

        const columnCount = getColumnCount(marketType);

        const sboMarketType = betBuilderToMarketType(marketType, statType);
        const recordedPoints = sboMarketType === undefined
            ? undefined
            : marketPointOrder.value.get(sboMarketType);
        const orderedMarkets = sortBetBuilderMarketsForDisplay(
            rawMarkets,
            isBetBuilderDisplayPriorityEnabled.value,
            unsortedMarkets => sortBetBuilderMarketsByRecordedOrder(unsortedMarkets, recordedPoints, sboMarketType),
        );
        const defaultMarkets = orderedMarkets.slice(0, columnCount);

        const displayMarkets = props.isOpenMoreMarket ? orderedMarkets : defaultMarkets;
        const marketList = processDisplayMarket(displayMarkets, columnCount);

        const collapseCount = orderedMarkets.length - defaultMarkets.length;

        return { marketList, collapseCount };
    }

    const mainMarketLayout = computed(() => {
        const formattedMarkets = new Map<BetBuilderStatType, Map<BetBuilderMarket, IBetBuilderMarket[][]>>();
        let collapseMainCount = 0;

        mainMatchStatType.forEach((statType) => {
            // handle when no market will only show full time market
            const hasMarket = markets.value
                .some(market => market.matchStatType.id === statType && mainMarketType.includes(market.marketType.id));

            if (!hasMarket && statType !== BetBuilderStatType.FullTime) {
                return;
            }

            const marketMap = new Map<BetBuilderMarket, IBetBuilderMarket[][]>();
            mainMarketType.forEach((marketType) => {
                const { marketList, collapseCount } = getMarketsForType(marketType, statType);
                marketMap.set(marketType, marketList);
                collapseMainCount += collapseCount;
            });
            formattedMarkets.set(statType, marketMap);
        });

        return { formattedMarkets, collapseMainCount };
    });

    const formattedBetBuilderMarkets = computed(() => mainMarketLayout.value.formattedMarkets);
    const collapseMainCount = computed(() => mainMarketLayout.value.collapseMainCount);

    watch(collapseMainCount, count => emit('update:collapseMainCount', count), { immediate: true });

    const isLive = computed(() => props.match.isLive);

    function isLastRow(statType: BetBuilderStatType) {
        const marketTypes = Array.from(formattedBetBuilderMarkets.value.keys());
        return marketTypes.indexOf(statType) === marketTypes.length - 1;
    }

    function getMarketTypeName(key: BetBuilderMarket | string) {
        const marketTypeMapping: Partial<Record<BetBuilderStatType | string, string>> = {
            [BetBuilderMarket.SetHandicap]: i18n.t('marketType_Handicap_Name'),
            [BetBuilderMarket.Set1x2]: i18n.t('marketType__1X2_Name'),
            [BetBuilderMarket.SetTotalGoalsOverUnder]: i18n.t('marketType_OverUnder_Name'),
        };

        return marketTypeMapping[key];
    }

    function getShortName(marketType: BetBuilderMarket) {
        return getMarketRuleByMarketType(marketType).shortNameDisplayList();
    }
</script>

<style lang="scss" scoped>
    @import "@/scss/components/betBuilderMatch";
    @import "@/scss/components/odd";
</style>
